import React from 'react';
import { Alert, Box, Button, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import { SignupStepUnavailable } from '../SignupStepUnavailable';
import { noticeSx } from '../../helpers/sx';

type Props = {
  code: string;
  error: string | null;
  linkNotice: string | null;
  confirmingLink: boolean;
  linkUnavailable: boolean;
  sessionMissing: boolean;
  submitting: boolean;
  resending: boolean;
  onCodeChange: (code: string) => void;
  onConfirm: () => void;
  onResend: () => void;
  onRetryLink: () => void;
  onLogIn: () => void;
  onStartOver: () => void;
  onChangeEmail: () => void;
};

export const SignupVerifyEmailView: React.FC<Props> = ({
  code,
  error,
  linkNotice,
  confirmingLink,
  linkUnavailable,
  sessionMissing,
  submitting,
  resending,
  onCodeChange,
  onConfirm,
  onResend,
  onRetryLink,
  onLogIn,
  onStartOver,
  onChangeEmail,
}) => {
  // In place of the form rather than above it: the code below belongs to the same email as the link that has
  // just failed to be reached, and offering it invites six digits that answer a different question.
  if (linkUnavailable) {
    return <SignupStepUnavailable onRetry={onRetryLink} />;
  }

  // Arriving from the emailed link, there is nothing to ask: the screen only reports progress. Ahead of the
  // screen below, which would answer a client the link is about to sign in with the wrong-browser notice.
  if (confirmingLink) {
    return (
      <Box maxWidth={520} mx="auto" py={4} textAlign="center">
        <CircularProgress />
        <Typography variant="body1" color="text.secondary" mt={3}>
          Confirming your email…
        </Typography>
      </Box>
    );
  }

  // The code below is checked against a session this browser does not have, and a new one would be sent to
  // the same place. Offering the form here is inviting six digits that are certain to be refused.
  if (sessionMissing) {
    return (
      <Box maxWidth={520} mx="auto">
        <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
          This browser has no signup in progress
        </Typography>
        <Stack spacing={3}>
          <Typography variant="body1" color="text.secondary">
            Your signup is waiting in the browser you started it in — open the email there and the
            confirmation button will pick it up. Confirming your email does not create the account: that
            happens when your company details are saved. Log in only if you got that far.
          </Typography>

          {/* Neither of these is the way forward for most people here, so neither is dressed as one. */}
          <Box display="flex" gap={2}>
            <Button variant="outlined" size="large" onClick={onLogIn}>
              Log in
            </Button>
            <Button variant="text" size="large" onClick={onStartOver}>
              Start a new signup
            </Button>
          </Box>
        </Stack>
      </Box>
    );
  }

  return (
    <Box maxWidth={520} mx="auto">
      <Typography variant="h4" fontWeight="fontWeightSemiBold" gutterBottom>
        Confirm your email
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        We sent you an email with a confirmation button and a six-digit code. Use either one.
      </Typography>

      {linkNotice != null && (
        <Alert severity="info" sx={noticeSx}>
          {linkNotice}
        </Alert>
      )}

      {error != null && (
        <Alert severity="error" sx={noticeSx}>
          {error}
        </Alert>
      )}

      <Stack spacing={3}>
        <TextField
          fullWidth
          label="Verification code"
          value={code}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          onChange={e => onCodeChange(e.target.value.replace(/\D/g, ''))}
        />

        <Button variant="contained" size="large" disabled={submitting} onClick={onConfirm}>
          {submitting ? 'Confirming…' : 'Confirm'}
        </Button>

        <Button variant="text" disabled={resending} onClick={onResend}>
          {resending ? 'Sending…' : 'Send a new code'}
        </Button>

        {/*
          The failure resending cannot mend: a code that reads perfectly well, sent where nobody is looking.
          One screen back rather than a new signup — the details are still this funnel's to correct until the
          confirmation freezes them.
        */}
        <Button variant="text" onClick={onChangeEmail}>
          Wrong email address? Change it
        </Button>
      </Stack>
    </Box>
  );
};
